import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface TriggerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema: string;
  tables: string[];
  functions: string[];
  onSuccess: () => void;
}

const TIMINGS = ["BEFORE", "AFTER", "INSTEAD OF"] as const;
const EVENTS = ["INSERT", "UPDATE", "DELETE", "TRUNCATE"] as const;
const FOR_EACH = ["ROW", "STATEMENT"] as const;

export function TriggerDialog({
  open,
  onOpenChange,
  schema,
  tables,
  functions,
  onSuccess,
}: TriggerDialogProps) {
  const [name, setName] = useState("");
  const [table, setTable] = useState("");
  const [timing, setTiming] = useState<string>("BEFORE");
  const [events, setEvents] = useState<Set<string>>(new Set(["INSERT"]));
  const [forEach, setForEach] = useState<string>("ROW");
  const [functionName, setFunctionName] = useState("");
  const [condition, setCondition] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName("");
    setTable("");
    setTiming("BEFORE");
    setEvents(new Set(["INSERT"]));
    setForEach("ROW");
    setFunctionName("");
    setCondition("");
    setError(null);
  }

  function toggleEvent(ev: string) {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) {
        if (next.size > 1) next.delete(ev);
      } else {
        next.add(ev);
      }
      return next;
    });
  }

  async function handleSubmit() {
    setError(null);

    if (!name.trim()) {
      setError("Trigger name is required");
      return;
    }
    if (!table) {
      setError("Table is required");
      return;
    }
    if (events.size === 0) {
      setError("At least one event is required");
      return;
    }
    if (!functionName) {
      setError("Function is required");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch("/api/database/triggers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schema,
          table,
          name: name.trim().toLowerCase(),
          timing,
          events: Array.from(events),
          forEach,
          functionSchema: schema,
          functionName,
          condition: condition.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "Failed to create trigger");
        return;
      }
      toast.success(`Trigger "${name}" created`);
      onSuccess();
      onOpenChange(false);
      reset();
    } catch (err: any) {
      setError(err.message || "Network error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <SheetContent side="right" className="sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle>Create Trigger</SheetTitle>
          <SheetDescription>
            Add a new trigger to the <span className="font-mono text-foreground">{schema}</span> schema.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="trigger-name" className="text-sm">Trigger Name</Label>
            <Input
              id="trigger-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my_trigger"
              className="h-8 text-sm font-mono"
            />
          </div>

          {/* Table */}
          <div className="space-y-1.5">
            <Label className="text-sm">Table</Label>
            <select
              value={table}
              onChange={(e) => setTable(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
            >
              <option value="">Select a table...</option>
              {tables.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Timing */}
          <div className="space-y-1.5">
            <Label className="text-sm">Timing</Label>
            <div className="flex gap-2">
              {TIMINGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTiming(t)}
                  className={`flex-1 h-8 rounded border text-sm font-medium transition-colors ${
                    timing === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-input text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Events */}
          <div className="space-y-1.5">
            <Label className="text-sm">Events</Label>
            <div className="flex gap-2 flex-wrap">
              {EVENTS.map((ev) => (
                <label
                  key={ev}
                  className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                    events.has(ev)
                      ? "bg-primary/10 text-primary border-primary/30"
                      : "bg-background border-input text-muted-foreground hover:border-foreground/30"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={events.has(ev)}
                    onChange={() => toggleEvent(ev)}
                    className="accent-primary"
                  />
                  {ev}
                </label>
              ))}
            </div>
          </div>

          {/* For Each */}
          <div className="space-y-1.5">
            <Label className="text-sm">For Each</Label>
            <div className="flex gap-2">
              {FOR_EACH.map((fe) => (
                <button
                  key={fe}
                  type="button"
                  onClick={() => setForEach(fe)}
                  className={`flex-1 h-8 rounded border text-sm font-medium transition-colors ${
                    forEach === fe
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background border-input text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  }`}
                >
                  {fe}
                </button>
              ))}
            </div>
          </div>

          {/* Function */}
          <div className="space-y-1.5">
            <Label className="text-sm">Execute Function</Label>
            <select
              value={functionName}
              onChange={(e) => setFunctionName(e.target.value)}
              className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
            >
              <option value="">Select a function...</option>
              {functions.map((f) => (
                <option key={f} value={f}>{f}()</option>
              ))}
            </select>
          </div>

          {/* Condition */}
          <div className="space-y-1.5">
            <Label className="text-sm">
              WHEN Condition <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="OLD.status IS DISTINCT FROM NEW.status"
              className="h-8 text-sm font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Boolean expression. Trigger fires only when this evaluates to true.
            </p>
          </div>

          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Trigger
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
